import {
  Objectclass,
  Pagination as PaginationType,
  ScrollBar,
  SortOption,
  YesObject
} from '@yes.technology/react-toolkit'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { useSiteState } from 'siteState/shared'
import useInitialStatus from 'status/shared/hooks/useInitialStatus'
import useLocationState from '../FilterFields/hooks/useLocationState'
import { ArrayFieldValue } from '../FilterFields/types'
import DataTableAreaLine from './DataTableAreaLine'
import Actions from './components/Actions'
import RenderEmbedded from './components/RenderEmbedded'
import TableActions from './components/TableActions/TableActions'
import TableBody from './components/TableBody'
import TableHeader from './components/TableHeader'
import useColumns from './hooks/useColumns'
import { TableContext, TableContextValue } from './hooks/useTableContext'
import useTableContextValue from './hooks/useTableContextValue'
import { TableLayout } from './hooks/useTableLayouts'
import {
  ActionColumn,
  ActionColumnsPosition,
  ExtraSelectionAction,
  HierarchyLevel,
  MapColumnToAction,
  SelectAndLinkOptions,
  SelectionAction,
  SelectionOption
} from './types'

export const defaultActionColumns = [
  {
    title: 'actions',
    component: Actions
  }
]

export type DataTableConfigProps = {
  layoutClassificationUuid?: string
  hierarchyLevel?: HierarchyLevel
  actionButton?: {
    type: 'view' | 'document' | null
    target: '_self' | '_blank'
    sitemodel?: string
  }
  hideRowActionButtons?: boolean
  hideMainCollapsibleContainer?: boolean
  hideSortButtons?: boolean
}

export type DataTableProps = DataTableConfigProps & {
  informational?: boolean
  embedded?: boolean
  idObjectClass: string
  fields: Objectclass['fields']
  objects: YesObject[]
  columns: string[]
  tableLayout: TableLayout
  isLoading: boolean
  actionColumns?: ActionColumn[]
  actionColumnsPosition?: ActionColumnsPosition
  onSort: (newSortOptions: SortOption[]) => void
  sortOptions?: SortOption[]
  pagination?: Partial<PaginationType>
  totalObjects?: number
  setCurrentPage: (page: number) => void
  title?: string
  isShowTitle?: boolean
  disableLinkButton?: boolean
  isCollapsibleCard?: boolean
  isShowPDFReports?: boolean
  isShowXLSReport?: boolean
  hideEditAction?: boolean
  layoutClassificationUuid?: string
  objectclassQueryString?: string
  displayLayoutSelection?: boolean
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  concatFields?: ConcatFields
  shouldResetObjectArea?: boolean
  setShouldResetObjectArea?: (newValue: boolean) => void
  style?: React.CSSProperties
  fieldSettings?: FieldSettings
  allowCollapsibleNavigationCardCustomColors?: boolean
  hideCollapsibleCardArrow?: boolean
  allowHighlightSelectedRow?: boolean
  selectAndLinkOptions?: SelectAndLinkOptions
  primarySelectionAction?: SelectionAction
  extraSelectionActions?: ExtraSelectionAction[]
  insertOnly?: boolean
  mapColumnToAction?: MapColumnToAction
} & Pick<
    TableContextValue,
    | 'objectclass'
    | 'mode'
    | 'setMode'
    | 'changedFieldValues'
    | 'setChangedFieldValues'
    | 'refetchObjects'
    | 'relationCounterpartField'
    | 'customActionButtons'
  >

const emptyArray: Array<unknown> = []

const parseOptionsToSelectedRows = (selectedOptions: ArrayFieldValue = []) =>
  selectedOptions.reduce((acc: Record<string, SelectionOption>, option) => {
    acc[option.uuid] = {
      uuid: option.uuid,
      des: option.des,
      value: option as YesObject
    }
    return acc
  }, {})

type SelectedRows = Record<string, SelectionOption>

const DataTable = ({
  idObjectClass,
  objectclass,
  fields,
  objects,
  refetchObjects,
  columns,
  isLoading,
  actionColumns = defaultActionColumns,
  actionColumnsPosition = 'last',
  onSort,
  tableLayout,
  sortOptions = emptyArray as SortOption[],
  pagination,
  totalObjects,
  setCurrentPage,
  title = '',
  isShowTitle = true,
  isCollapsibleCard = false,
  layoutClassificationUuid,
  hierarchyLevel = 'level_2',
  actionButton,
  mode,
  setMode,
  changedFieldValues,
  setChangedFieldValues,
  relationCounterpartField,
  objectclassQueryString = 'uuid_objectclass',
  displayLayoutSelection = true,
  informational = false,
  embedded = false,
  navigationSliderBackgroundColor = '#004099',
  navigationSliderColor,
  concatFields,
  shouldResetObjectArea,
  setShouldResetObjectArea,
  hideMainCollapsibleContainer = false,
  hideRowActionButtons = false,
  hideSortButtons = false,
  hideEditAction = false,
  style,
  fieldSettings,
  customActionButtons,
  allowCollapsibleNavigationCardCustomColors,
  selectAndLinkOptions,
  primarySelectionAction,
  isShowPDFReports,
  isShowXLSReport,
  extraSelectionActions = emptyArray as ExtraSelectionAction[],
  disableLinkButton,
  allowHighlightSelectedRow,
  insertOnly = false,
  mapColumnToAction,
  hideCollapsibleCardArrow
}: DataTableProps) => {
  const { selectedOptions: locationStateObjects } = useLocationState()

  const [searchParams] = useSearchParams()

  const searchParamsObjectsString = searchParams.get('selected_objects')
  const searchParamsObjects = useMemo(
    () =>
      searchParamsObjectsString ? JSON.parse(searchParamsObjectsString) : [],
    [searchParamsObjectsString]
  )

  const [selectedRows, setSelectedRows] = useState<SelectedRows>(
    parseOptionsToSelectedRows(locationStateObjects)
  )

  useEffect(() => {
    if (Array.isArray(locationStateObjects)) {
      setSelectedRows(parseOptionsToSelectedRows(locationStateObjects))
    }
  }, [locationStateObjects])

  useEffect(() => {
    if (Array.isArray(searchParamsObjects)) {
      setSelectedRows(parseOptionsToSelectedRows(searchParamsObjects))
    }
  }, [searchParamsObjects])

  const hasSelectionAction = !informational && mode === 'view'

  const showColumn =
    !embedded || actionButton?.type === 'document' || !informational

  const amountOfActionColumns =
    showColumn && !hideRowActionButtons
      ? actionColumns.length + (hasSelectionAction ? 1 : 0)
      : 0

  const {
    displayedColumns,
    currentColumnIndex,
    totalColumns,
    amountOfFixedColumns,
    onChangeColumnsPage,
    allColumns
  } = useColumns({
    tableLayout,
    amountOfActionColumns
  })

  const tableContextValue = useTableContextValue({
    idObjectClass,
    objectclass,
    fields,
    actionColumns,
    actionColumnsPosition,
    columnsNames: displayedColumns,
    amountOfFixedColumns,
    totalColumns,
    objects,
    refetchObjects,
    columnsLayouts: tableLayout.columnsLayouts,
    readOnlyColumns: tableLayout.readOnlyColumns,
    onSort,
    sortOptions,
    currentColumnIndex,
    pagination,
    totalObjects,
    layoutClassificationUuid,
    hierarchyLevel,
    actionButton,
    selectedRows,
    setSelectedRows,
    mode,
    setMode,
    changedFieldValues,
    setChangedFieldValues,
    relationCounterpartField,
    objectclassQueryString,
    displayLayoutSelection,
    informational,
    embedded,
    navigationSliderBackgroundColor,
    navigationSliderColor,
    allowCollapsibleNavigationCardCustomColors,
    concatFields,
    hideRowActionButtons,
    hideSortButtons,
    hideEditAction,
    onChangeColumnsPage,
    amountOfActionColumns,
    customActionButtons,
    allColumns,
    selectAndLinkOptions,
    primarySelectionAction,
    isShowPDFReports,
    isShowXLSReport,
    extraSelectionActions,
    disableLinkButton,
    allowHighlightSelectedRow,
    insertOnly,
    mapColumnToAction
  })

  const initialStatus = useInitialStatus({ objectclassUuid: idObjectClass })

  const navigateMergingState = useNavigateMergingState()

  const [_, setObjectIndex] = useSiteState<number | undefined>('object-index')
  const [lockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  const paginationOffset = pagination?.offset || 0

  useEffect(() => {
    const handleSelectedObjectNavigation = () => {
      searchParams.set('object-uuid', objects[0].uuid)
      searchParams.set(objectclassQueryString, idObjectClass)

      const search = `?${searchParams.toString()}`

      setObjectIndex(paginationOffset)

      setShouldResetObjectArea?.(false)

      navigateMergingState(
        {
          search,
          hash: ''
        },
        {
          state: {
            sortOptions
          },
          replace: true
        }
      )
    }
    if (
      lockObjectViewObject ||
      hideRowActionButtons ||
      objects.length === 0 ||
      !shouldResetObjectArea
    )
      return

    handleSelectedObjectNavigation()
  }, [
    lockObjectViewObject,
    objects,
    navigateMergingState,
    objectclassQueryString,
    idObjectClass,
    sortOptions,
    embedded,
    setObjectIndex,
    shouldResetObjectArea,
    setShouldResetObjectArea,
    hideRowActionButtons,
    paginationOffset,
    searchParams
  ])

  return (
    <TableContext.Provider value={tableContextValue}>
      <DataTableAreaLine
        {...{
          hierarchyLevel,
          title,
          isShowTitle,
          isCollapsibleCard,
          navigationSliderBackgroundColor,
          navigationSliderColor,
          pagination,
          setCurrentPage,
          embedded,
          hideMainCollapsibleContainer,
          hideCollapsibleCardArrow,
          allowCollapsibleNavigationCardCustomColors
        }}
        aria-busy={isLoading}
        aria-live='assertive'
        style={style}
        layoutClassificationUuid={layoutClassificationUuid}
      >
        <RenderEmbedded className='pb-4' embedded={embedded}>
          <div className='row'>
            <div className='col-md-12'>
              <div className='table-responsive'>
                <ScrollBar scrollMode='horizontal'>
                  {!informational && !isLoading && (
                    <TableActions
                      {...{
                        layoutClassificationUuid,
                        initialStatus
                      }}
                    />
                  )}
                  <table className='table'>
                    <TableHeader {...{ isLoading }} />
                    <TableBody {...{ isLoading, fieldSettings }} />
                  </table>
                </ScrollBar>
              </div>
            </div>
          </div>
        </RenderEmbedded>
      </DataTableAreaLine>
    </TableContext.Provider>
  )
}

export default DataTable
